import { Box, Grid } from "@mui/material"
import { TabPanel } from "components/navigation/TabPanel"
import SubTabs from "components/SubTabs"
import { useEffect, useState } from "react"
import CustomFieldGroups from "./components/customFieldGroup"
import CustomFields from "./components/customFields"
import { useTranslation } from "react-i18next"
import { useApi } from "auth/useApi3"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { apiEndpoints } from "generated/apiEndpoints"
import { CreateTemplateModal } from "pages/vehicleregistry/pages/settings/checktemplates/components/CreateTemplateModal/CreateTemplateModal"
import { CheckTemplateCreate, CheckTemplateIntervalType, CheckTemplateSourceType, CheckTemplateUpdate, IntervalCalculationType } from "pages/vehicleregistry/pages/settings/checktemplates/types/CheckTypes.interfaces"
import { formatDateForApi } from "components/DateHandler/dateHandler"
import { SnackbarState } from "pages/vehicleregistry/pages/settings/checktemplates/components/CheckTemplates/CheckTemplates.interfaces"
import VehicleGroupsTab from "pages/vehicleregistry/pages/settings/checktemplates/components/VehicleGroupsTab/VehicleGroupsTab"
import CheckTemplatesTab from "./checktemplates/components/CheckTemplates/CheckTemplates"
import SuccessDialog from "components/Dialogs/SuccessDialog"
import ThemedSubTabs from "components/ThemedSubTabs"

const getTabData = (t: any) => {
  return [
    { index: 0, label: t("global.custom_fields", "Custom fields"), queryParam: "customerSettings" },
    { index: 1, label: t("custom_fields.couple_custom_fields", "Couple custom fields"), queryParam: "vehiclegroups" },
    { index: 2, label: t("global.vehicle_groups", "Vehicle groups"), queryParam: "groups" },
    { index: 3, label: t("global.check_templates", "Check templates"), queryParam: "checktemplates" }
  ]
}

const Settings = () => {
  const [checkTemplateResult, checkTemplateApi] = useApi() as [IApiResult, IApiFunc]
  const [templatesListResult, templatesListApi] = useApi() as [IApiResult, IApiFunc]
  const [deleteTemplateResult, deleteTemplateApi] = useApi() as [IApiResult, IApiFunc]
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [tabValue, setTabValue] = useState(0)
  const [selectedTemplate, setSelectedTemplate] = useState<any>(undefined)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [templateDetailsResult, templateDetailsApi] = useApi() as [IApiResult, IApiFunc]
  const { t } = useTranslation()
  const tabData = getTabData(t)
  const [showDialog, setShowDialog] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const snackBarErrorFetch: SnackbarState = {
    title: t("dashboard.something_went_wrong", "Something went wrong"),
    message: t("checks.failed_checktemplate_details", "Failed to retrieve check template details"),
  }

  const snackBarSuccess: SnackbarState = {
    title: isEditing ? t("global.done_dialog", "Done!") : t("global.done_dialog", "Done!"),
    message: isEditing ? t("checks.check_template_updated", "Check template updated") : t("checks.check_template_created", "Check template created"),
  }

  const [snackbar, setSnackbar] = useState<SnackbarState>(snackBarSuccess)

  useEffect(() => {
    return () => {
      checkTemplateApi.leave()
      templatesListApi.leave()
      templateDetailsApi.leave()
      deleteTemplateApi.leave()
    }
  }, [checkTemplateApi, templatesListApi, templateDetailsApi, deleteTemplateApi])

  useEffect(() => {
    templatesListApi.get(apiEndpoints.checktemplate.getchecktemplatelist)
  }, [refreshTrigger, templatesListApi])

  useEffect(() => {
    if (checkTemplateResult.status === 2) {
      setSnackbar(snackBarSuccess)
      setShowDialog(true)
      setCreateModalOpen(false)
      checkTemplateApi.reset()
      setRefreshTrigger(prev => prev + 1)
    } else if (checkTemplateResult.status === 3) {
      const errorMessage = (checkTemplateResult.messages && checkTemplateResult.messages.length > 0)
        ? checkTemplateResult.messages[0]
        : t("checks.error_creating_check_template", "An error occurred while creating the template")
      setSnackbar({
        title: t("dashboard.something_went_wrong", "Something went wrong"),
        message: `${t("checks.problem_creating_check_template", "A problem occured while creating template")}: ${errorMessage}`,
      })
      setShowDialog(true)
      checkTemplateApi.reset()
    }
  }, [checkTemplateResult, checkTemplateApi])

  useEffect(() => {
    if (templateDetailsResult.status === 2 && templateDetailsResult.data) {
      setSelectedTemplate(templateDetailsResult.data)
      setCreateModalOpen(true)
      templateDetailsApi.reset()
    } else if (templateDetailsResult.status === 3) {
      setSnackbar(snackBarErrorFetch)
      setShowDialog(true)
      templateDetailsApi.reset()
    }
  }, [templateDetailsResult, templateDetailsApi])

  useEffect(() => {
    if (deleteTemplateResult.status === 2) {
      setShowDialog(true)
      setSnackbar({
        title: t("global.success", "Success"),
        message: t("checks.check_template_deleted", "Check template deleted"),
      })
      deleteTemplateApi.reset()
      setRefreshTrigger(prev => prev + 1)
    } else if (deleteTemplateResult.status === 3) {
      const errorMessage = (deleteTemplateResult.messages && deleteTemplateResult.messages.length > 0)
        ? deleteTemplateResult.messages[0]
        : t("checks.problem_deleting_check_template", "An error occurred while deleting the template")
      setSnackbar({
        title: t("dashboard.something_went_wrong", "Something went wrong"),
        message: `${t("checks.problem_deleting_check_template2", "Error deleting template")}: ${errorMessage}`,
      })
      setShowDialog(true)
      deleteTemplateApi.reset()
    }
  }, [deleteTemplateResult, deleteTemplateApi])

  const handleCreateTemplate = () => {
    setSelectedTemplate(undefined)
    setCreateModalOpen(true)
  }

  const handleCloseModal = () => {
    setCreateModalOpen(false)
    setSelectedTemplate(undefined)
    checkTemplateApi.reset()
  }

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }))
    setShowDialog(false)
  }

  const handleTemplateComplete = (templateData: any) => {
    const isEditing = !!templateData.id

    const checkTemplateCreate: CheckTemplateCreate = {
      name: templateData.name,
      vehicleIds: templateData.selectedVehicles,
      intervals: [],
      vehicleServices: []
    }

    if (templateData.usesDate && templateData.dateInterval) {
      checkTemplateCreate.intervals.push({
        type: CheckTemplateIntervalType.Days,
        value: templateData.dateInterval,
        sourceType: CheckTemplateSourceType.Manual,
        calculationType: templateData.dateCalculationType === 'Static' ? IntervalCalculationType.Static : IntervalCalculationType.Dynamic
      })
    }

    if (templateData.usesDistance && templateData.distanceInterval) {
      checkTemplateCreate.intervals.push({
        type: CheckTemplateIntervalType.Distance,
        value: templateData.distanceInterval,
        sourceType: CheckTemplateSourceType.Manual,
        calculationType: templateData.distanceCalculationType === 'Static' ? IntervalCalculationType.Static : IntervalCalculationType.Dynamic
      })
    }

    if (templateData.usesInspection) {
      checkTemplateCreate.intervals.push({
        type: CheckTemplateIntervalType.Days,
        sourceType: CheckTemplateSourceType.TransportStyrelsen,
        calculationType: IntervalCalculationType.Dynamic
      })
    }

    if (templateData.vehicleLastValues) {
      Object.keys(templateData.vehicleLastValues).forEach((vehicleId) => {
        const vehicleData = templateData.vehicleLastValues[vehicleId]

        if (templateData.usesDate && vehicleData.lastDate) {
          const lastDate = formatDateForApi(vehicleData.lastDate)
          checkTemplateCreate.vehicleServices.push({
            vehicleId: parseInt(vehicleId),
            lastCompletedValue: lastDate
          })
        }

        if (templateData.usesDistance && vehicleData.lastDistance !== undefined) {
          checkTemplateCreate.vehicleServices.push({
            vehicleId: parseInt(vehicleId),
            lastCompletedValue: parseFloat(vehicleData.lastDistance).toString()
          })
        }
      })
    }

    if (isEditing) {
      setIsEditing(true)
      const checkTemplateUpdate: CheckTemplateUpdate = { ...checkTemplateCreate, id: templateData.id }
      const url = apiEndpoints.checktemplate.updatechecktemplate
      checkTemplateApi.put(url, checkTemplateUpdate)
    } else {
      const url = apiEndpoints.checktemplate.createchecktemplate
      checkTemplateApi.post(url, checkTemplateCreate)
    }
  }

  function editCheckTemplate(template: any) {
    const templateUrl = apiEndpoints.checktemplate.getchecktemplate.replace('{id}', template.id)
    templateDetailsApi.get(templateUrl)
  }

  function deleteCheckTemplate(templateId: number) {
    const deleteUrl = apiEndpoints.checktemplate.deletechecktemplate.replace('{id}', templateId.toString())
    deleteTemplateApi.del(deleteUrl)
  }

  return (
    <Box>
      <ThemedSubTabs tabs={tabData} value={tabValue} setValue={setTabValue} />
      <Grid container>
        <Grid item xs={12}>
          <TabPanel value={tabValue} index={0}>
            <CustomFields />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <CustomFieldGroups />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <VehicleGroupsTab />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <CheckTemplatesTab
              onCreateTemplate={handleCreateTemplate}
              onEditTemplate={editCheckTemplate}
              onDeleteTemplate={deleteCheckTemplate}
              templatesListResult={templatesListResult}
              deleteTemplateResult={deleteTemplateResult}
            />
          </TabPanel>
        </Grid>

        <CreateTemplateModal
          open={createModalOpen}
          onClose={handleCloseModal}
          onComplete={handleTemplateComplete}
          checkTemplate={selectedTemplate}
          checkTemplateResult={checkTemplateResult}
          checkTemplateListResult={templatesListResult}
        />

        {showDialog && (
          <SuccessDialog
            resetModalState={handleSnackbarClose}
            message={snackbar.message ?? ""}
            title={snackbar.title ?? ""}
          />
        )}
      </Grid>
    </Box>
  )
}

export default Settings
